import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import { executeQuery } from '@arcgis/core/rest/query';
import LocatorSearchSource from '@arcgis/core/widgets/Search/LocatorSearchSource';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import VectorTileLayer from '@arcgis/core/layers/VectorTileLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import Layer from '@arcgis/core/layers/Layer';
import Field from '@arcgis/core/layers/support/Field';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import * as jsonUtils from '@arcgis/core/geometry/support/jsonUtils';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import TextSymbol from '@arcgis/core/symbols/TextSymbol';
import SimpleRenderer from '@arcgis/core/renderers/SimpleRenderer';
import Query from '@arcgis/core/rest/support/Query';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import pubSub from '../../utils/pubSub';
import debounce from 'lodash/debounce';
import moment from 'moment';
import originFields from '../../defaults/PointOfOriginFields.json'
import uncertifiedPerimeterFields from '../../defaults/UncertifiedPerimeterFields.json'
import { getSQLDate, getSQLDateEndOfDay } from '../../utils/incidentHelper';
import consts from '../../defaults/Enums.json'
import symbols from '../../defaults/Symbols.json'
import { createLegend } from '../../utils/esriLoader';
import { getAcresFieldName, getBeneAcresFieldName } from '../../utils/incidentHelper';

export default class LayerManager {

  constructor(view, config, search, isReadOnly) {

    this.shouldFilterOnPerimeters = false;
    this.perimeterMapMethod = null;
    this.perimeterFeatureStatus = null;
    this.perimeterFeatureAccess = null;
    this.capabilityRequestOption = null;
    this.spatialFilterPolygon = null;
    this.maxCountForIncidents = 1000;
    this.searchWidget = search;

    return new Promise((resolve, reject) => {
      this.view = view;
      this.map = view.map;
      this.config = config;
      this.isReadOnly = isReadOnly;

      this.initLayers()
        .then(
          result => {
            createLegend(this.view, this.originFeatureLayer, this.viirsFeatureLayer);
            window.api.setOriginApiUrl(this.config.originApiUrl[this.config.env]);
            this.initEventHandlers();
            this.initSearch(search);

            if (this.jurisdictionalFeatureLayer) 
            {
              window.api.setJurisdictionalFeatureLayerUrl(this.jurisdictionalFeatureLayer.url + '/0');
              window.api.setJurisdictionalFeatureLayerToken(this.jurisdictionalFeatureLayer.portalItem.portal.credential.token)
            }
            resolve();
          },
          error => {
            reject();
          }
        );
    });
  }

  initSearch(searchWidget) {
    searchWidget.sources.removeAll();

        searchWidget.sources.add({
          layer: new FeatureLayer({
            url: this.originFeatureLayer.url + '/' + this.originFeatureLayer.layerId,
            outFields: ["OBJECTID", "IncidentName", "UniqueFireIdentifier", "FORID"]
          }),
          searchFields: this.config.originLayer.search.fields,
          exactMatch: false,
          outFields: ["OBJECTID", "IncidentName", "UniqueFireIdentifier", "FORID"],
          name: "Incidents",
          maxSuggestions: 10,
          suggestionsEnabled: true,
          suggestionTemplate: "Name: {IncidentName} \nID: {UniqueFireIdentifier}",
          minSuggestCharacters: 0,
          withinViewEnabled: false,
          resultGraphicEnabled: false,
          popupTemplate: this.originPopupTemplate
        },0);

        searchWidget.sources.add(new LocatorSearchSource({
          url: this.config.referenceLayerEndpoints.geocode,
          singleLineFieldName: "SingleLine",
          outFields: ["Addr_type"],
          name: "US Locator",
          countryCode: 'US',
          localSearchOptions: {
            minScale: 300000,
            distance: 50000
          },
          maxResults: 3,
          maxSuggestions: 3
        }), 1);
        searchWidget.activeSourceIndex = -1;
  }


  initEventHandlers() {
    console.log("initEventHandlers run");
        pubSub.subscribe('LayerManager/get-origin-definition-expression', this.handleGetOriginDefinitionExpression.bind(this));
        pubSub.subscribe('LayerManager/get-origin-fields', this.handleGetOriginFields.bind(this));
        pubSub.subscribe('LayerManager/get-origin-features', this.handleGetOriginFeatures.bind(this));
        pubSub.subscribe('LayerManager/get-full-origin-features-from-api', this.handleGetFullOriginFeaturesFromApi.bind(this));
        pubSub.subscribe('LayerManager/get-origin-feature', this.handleGetOriginFeature.bind(this));
        pubSub.subscribe('LayerManager/set-origin-selection-graphics', this.handleSetOriginSelectionGraphics.bind(this));
        pubSub.subscribe('LayerManager/set-origin-selection-graphic', this.handleSetOriginSelectionGraphic.bind(this));
        pubSub.subscribe('LayerManager/set-merge-graphic', this.handleSetMergeGraphic.bind(this));
        pubSub.subscribe('LayerManager/clear-merge-graphics', this.handleClearMergeGraphics.bind(this));
        pubSub.subscribe('LayerManager/set-origin-definition-expression', debounce(this.handleSetOriginDefinitionExpression.bind(this), 500));
        pubSub.subscribe('LayerManager/set-custom-filter-definition-expression', this.handleSetCustomFilterDefinitionExpression.bind(this));
        pubSub.subscribe('LayerManager/set-visible-perimeters', this.handleSetVisiblePerimeters.bind(this));
        pubSub.subscribe('LayerManager/get-forids-for-query', this.handleGetFORIDsForQuery.bind(this));
        pubSub.subscribe('LayerManager/add-merged-perimeter-graphic', this.handleAddMergedPerimeterGraphic.bind(this));     
        pubSub.subscribe('LayerManager/set-perimeter-graphic', this.handleSetPerimeterGraphic.bind(this));       
        pubSub.subscribe('LayerManager/clear-perimeter-graphics', this.handleClearPerimeterGraphics.bind(this)); 
        pubSub.subscribe('LayerManager/clear-merged-perimeter-graphics', this.handleClearMergedPerimeterGraphics.bind(this));  
        pubSub.subscribe('LayerManager/set-modified-perimeter', this.handleSetModifiedPerimeter.bind(this));
        pubSub.subscribe('LayerManager/set-modified-perimeter-no-clear', this.handleSetModifiedPerimeterNoClear.bind(this));
        pubSub.subscribe('LayerManager/add-debug-graphic', this.handleAddDebugGraphic.bind(this));
        pubSub.subscribe('LayerManager/set-perimeters-symbology', this.handleSetPerimetersSymbol.bind(this));
        pubSub.subscribe('LayerManager/set-origin-visible', this.handleSetOriginVisible.bind(this));
        pubSub.subscribe('LayerManager/clear-visible-perimeters', this.handleClearVisiblePerimeters.bind(this));
        pubSub.subscribe('LayerManager/clear-origin-selection-graphics', this.handleClearOriginSelectionGraphics.bind(this));
        pubSub.subscribe('LayerManager/clear-modified-perimeter', this.handleClearModifiedPerimeter.bind(this));
        pubSub.subscribe('LayerManager/origin-refresh', this.handleOriginRefresh.bind(this));
        pubSub.subscribe('LayerManager/is-origin-within-perimeter', this.handleOriginWithinPerimeter.bind(this));
        pubSub.subscribe('LayerManager/on-update-end-origin-layer', this.handleOriginUpdateEnd.bind(this));
        pubSub.subscribe('LayerManager/set-spatial-filter-polygon', this.handleSetSpatialFilterPolygon.bind(this));
        pubSub.subscribe('LayerManager/add-user-layer-to-map', this.handleAddUserLayerToMap.bind(this));
        pubSub.subscribe('LayerManager/add-and-save-user-layer', this.handleAddAndSaveUserLayer.bind(this));
        pubSub.subscribe('LayerManager/remove-user-layer', this.handleRemoveUserLayer.bind(this));
  }

  initLayers() {
    return new Promise((resolve, reject) => {

          this.heatmapRenderer = {
            type: "heatmap",
            colorStops: [
              { ratio: 0, color: "rgba(255, 255, 255, 0)" },
              { ratio: 0.2, color: "rgba(255, 255, 255, 1)" },
              { ratio: 0.5, color: "rgba(255, 140, 0, 1)" },
              { ratio: 0.8, color: "rgba(255, 140, 0, 1)" },
              { ratio: 1, color: "rgba(255, 0, 0, 1)" }
            ],
            maxPixelIntensity: 500,
            minPixelIntensity: 100
          };

          this.originPopupTemplate = {
            title: `Incident Name: {${originFields.name}}`,
            alignment: "bottom-center",
            actions: [
              {
                title: "View Incident",
                id: "popup-view-incident",
                className: "esri-icon-review"
              },
              {
                title: "Zoom To",
                id: "popup-zoom-to-incident",
                className: "esri-icon-zoom-in-magnifying-glass"
              }
            ],
            overwriteActions: true,
            fieldInfos: [
              {
                fieldName: "FORID",
                label: "FORID"
              },
              {
                fieldName: "IncidentSize",
                label: "Incident Size",
              },
              {
                fieldName: "FireCode",
                label: "Fire Code"
              },
              {
                fieldName: "FireDiscoveryDateTime",
                label: "Discovery",
                /*format:
                {
                  dateFormat: "short-date"
                }*/
              },
              {
                fieldName: "FireOutDateTime",
                label: "Fire Out",
                /*format:
                {
                  dateFormat: "short-date"
                }*/
              } 
            ],
            expressionInfos:
              [
                {
                  name: "arcade-for-id",
                  expression: "DefaultValue($feature.FORID, '--')"
                },
                {
                  name: "arcade-incident-size",
                  expression: "DefaultValue($feature.IncidentSize, '--')"
                },
                {
                  name: "arcade-fire-code",
                  expression: "DefaultValue($feature.FireCode, '--')"
                },
                {
                  name: "arcade-discovery-date",
                  expression: "When(IsEmpty($feature.FireDiscoveryDateTime), '--', Text(Date($feature.FireDiscoveryDateTime), 'MM/DD/YYYY'))"
                },
                {
                  name: "arcade-fire-out-date",
                  expression: "When(IsEmpty($feature.FireOutDateTime), '--', Text(Date($feature.FireOutDateTime), 'MM/DD/YYYY'))"
                }
              ],
            content: [
              {
                type: "text",
                text:
                  '<div class="popup-incident-primary-fields">' +
                  '<div class="list-item-detail-group">' +
                  '<div class="list-item-field-group">' +
                  '<label>Incident Name</label>' +
                  '<p class="list-item-incident-name">{IncidentName}</p>' +
                  '</div>' +
                  '<div class="list-item-field-group">' +
                  '<label>ID</label>' +
                  '<p class="list-item-detail-label">{UniqueFireIdentifier}</p></div></div>' +
                  '<div class="list-item-detail-group">' +
                  '<div class="list-item-field-group">' +
                  '<label>Incident Size</label>' +
                  '<p>{expression/arcade-incident-size}</p>' +
                  '</div>' +
                  '<div class="list-item-field-group">' +
                  '<label>FireCode</label>' +
                  '<p>{expression/arcade-fire-code}</p>' +
                  '</div>' +
                  '</div>' +
                  '<div class="list-item-detail-group">' +
                  '<div class="list-item-field-group">' +
                  '<label>Discovery</label>' +
                  '<p>{expression/arcade-discovery-date}</p>' +
                  '</div>' +
                  '<div class="list-item-field-group">' +
                  '<label>Fire Out</label>' +
                  '<p>{expression/arcade-fire-out-date}</p>' +
                  '</div>' +
                  '</div>' +
                  '</div>'
              }
            ]
          };

          this.pointRenderer = {
            type: "unique-value",
            valueExpression: `
                   var notUnique = (!IsEmpty($feature.MinUniqueness) && ($feature.MinUniqueness <= .25));
                   var nonWildfire = ($feature.IncidentTypeCategory != 'RX' && $feature.IncidentTypeCategory != 'WF');
                   
                   var isComplete = ($feature.StatusID == 1);
                   var isCompleteAndNonUnique = (isComplete && notUnique);
                   var isCompleteAndUnique = (isComplete && !notUnique);
                  
                   var isCertified = ($feature.StatusID == 3);
                   var isCertifiedAndNonUnique = (isCertified && notUnique); 
                   var isCertifiedUnique = (isCertified && !notUnique); 
                   
                   var default = (!isCertified && !isComplete);
                   var defaultAndNonUnique = (default && notUnique);
                   var defaultAndUnique = (default && !notUnique);

                   var nonWildFireIncomplete = (nonWildfire && !isComplete && !isCertified);
                   var nonWildFireComplete = (nonWildfire && isComplete && !isCertified);
                   
                   var isQuarantined = ($feature.IsQuarantined == 1);
                   var prescribed = ($feature.IncidentTypeCategory == 'RX');
                 
                   var notUniqueAndPrescribed = (prescribed && notUnique && !isCertified);
                   var uniqueAndPrescribed = (prescribed && !notUnique && !isCertified);
                   var nonWildFireNonUnique = (nonWildFireIncomplete && notUnique && !isCertified));
                   return When($feature.IsQuarantined == 1, 'IsQuarantined', 
                   isCompleteAndNonUnique, 'CompleteLowUniqueness', isCompleteAndUnique, 'Complete',  
                   isCertifiedAndNonUnique, 'CertifiedLowUniqueness', isCertifiedUnique, 'Certified', 
                   defaultAndNonUnique, 'IncompleteLowUniqueness', defaultAndUnique, 'Incomplete',
                   notUniqueAndPrescribed, 'PrescribedFireLowUniqueness', uniqueAndPrescribed, 'PrescribedFire',
                   nonWildFireComplete, 'NonWildfireComplete', nonWildFireIncomplete, 'NonWildfire', 'Incomplete')`,
            uniqueValueInfos: [
              {
                value: 'Certified',
                label: "Certified",
                symbol: symbols.certifiedSymbol
              },
              {
                value: 'CertifiedLowUniqueness',
                label: "Certified - Low Uniqueness",
                symbol: symbols.certifiedLowUniquenessSymbol
              },
              {
                value: consts.incidentStatus.complete,
                label: "Complete",
                symbol: symbols.completeSymbol
              },
              {
                value: 'CompleteLowUniqueness',
                label: "Complete - Low Uniqueness",
                symbol: symbols.completeLowUniquenessSymbol
              },
              {
                value: "Incomplete",
                label: "Incomplete",
                symbol: symbols.defaultIncidentSymbol
              },
              {
                value: 'IncompleteLowUniqueness',
                label: "Incomplete - Low Uniqueness",
                symbol: symbols.defaultLowUniquenessIncidentSymbol
              },
              {
                value: 'IsQuarantined',
                label: "Is Quarantined",
                symbol: symbols.conflictIncidentSymbol
              },
              {
                value: 'PrescribedFire',
                label: "Prescribed Fire",
                symbol: symbols.prescribedFireSymbol
              },
              {
                value: 'PrescribedFireLowUniqueness',
                label: "Prescribed - Low Uniqueness",
                symbol: symbols.prescribedFireLowUniquenessSymbol
              },
              {
                value: 'NonWildfire',
                label: "Other Fire Type",
                symbol: symbols.otherFireSymbol
              },
              {
                value: 'NonWildFireLowUniqueness',
                label: "Other Fire Type - Low Uniqueness",
                symbol: symbols.otherFireLowUniquenessSymbol
              },
              {
                value: 'NonWildfireComplete',
                label: "Other Fire Type - Complete",
                symbol: symbols.otherFireSymbolComplete
              }
            ],
            visualVariables: [{
              type: "size",
              field: "IncidentSize",
              stops: [
                { value: 0.25, size: 10, label: "Class A" },
                { value: 10, size: 14, label: "Class B" },
                { value: 100, size: 18, label: "Class C" },
                { value: 300, size: 22, label: "Class D" },
                { value: 1000, size: 26, label: "Class E" },
                { value: 5000, size: 30, label: "Class F" },
                { value: 50000, size: 34, label: "Class G" }
              ]
            }]
          };

          let originConfig = {
            portalItem: { id: this.config.originLayer.portalItemId },
            definitionExpression: this.config.originLayer.defaultWhere,
            outFields: [...new Set([...originFields.requiredOutFields])],
            visible: true,
            recordCount: 20,
            //renderer: this.pointRenderer,
            popupTemplate: this.originPopupTemplate
          };


          this.fodrGraphicsLayer = new GraphicsLayer();
          this.fodrGraphicsLayer.renderer = this.pointRenderer;
          this.fodrGraphicsLayer.visible = true;
          this.fodrGraphicsLayer.listMode = "hide";

          this.originFeatureLayer = new FeatureLayer(Object.assign({}, originConfig, this.config.originLayer.jsInit[this.config.env]));
          this.originFeatureLayer.listMode = "hide";
          this.originFeatureLayer.visible = true;
          this.originFeatureLayer.renderer = this.pointRenderer;

          this.jurisdictionalRenderer = {
            type: "unique-value",
            field: "JurisdictionalCategory",  //TODO: Fix hardcode
            uniqueValueInfos: [
              {
                value: "ANSCA",
                label: "ANSCA",
                symbol: symbols.jurisdictionalLayer.TribalSymbol
              },
              {
                value: "BIA",
                label: "BIA",
                symbol: symbols.jurisdictionalLayer.TribalSymbol
              },
              {
                value: "Tribal",
                label: "Tribal",
                symbol: symbols.jurisdictionalLayer.TribalSymbol
              },
              {
                value: "BLM",
                label: "Bureau of Land Management (BLM)",
                symbol: symbols.jurisdictionalLayer.BLMSymbol
              },
              {
                value: "BOR",
                label: "Bureau of Reclamation",
                symbol: symbols.jurisdictionalLayer.BORSymbol
              },
              {
                value: "DOD",
                label: "Military Reservations and Corps of Engineer",
                symbol: symbols.jurisdictionalLayer.MRCESymbol
              },
              {
                value: "DOE",
                label: "Military Reservations and Corps of Engineer",
                symbol: symbols.jurisdictionalLayer.MRCESymbol
              },
              {
                value: "NPS",
                label: "National Park Service (NPS)",
                symbol: symbols.jurisdictionalLayer.NPSSymbol
              },
              {
                value: "OtherFed",
                label: "OtherFederal",
                symbol: symbols.jurisdictionalLayer.OtherFedSymbol
              },
              {
                value: "State",
                label: "State",
                symbol: symbols.jurisdictionalLayer.StateSymbol
              },
              {
                value: "City",
                label: "City/County",
                symbol: symbols.jurisdictionalLayer.CityCountySymbol
              },
              {
                value: "Private",
                label: "Private",
                symbol: symbols.jurisdictionalLayer.PrivateSymbol
              },
              {
                value: "USACOE",
                label: "Corps of Engineer",
                symbol: symbols.jurisdictionalLayer.MRCESymbol
              },
              {
                value: "USFS",
                label: "US Forest Service (USFS)",
                symbol: symbols.jurisdictionalLayer.ForestServiceSymbol
              },
              {
                value: "USFWS",
                label: "US Fish and Wildlife (USFW) National Wildlife Refuge",
                symbol: symbols.jurisdictionalLayer.USFWSSymbol
              },
              {
                value: "USGS",
                label: "USGS",
                symbol: symbols.jurisdictionalLayer.OtherFedSymbol
              },
              {
                value: "USCG",
                label: "USCG",
                symbol: symbols.jurisdictionalLayer.MRCESymbol
              }
            ]
          };

          let jurisdictionalFeatureConfig = {
            portalItem: { id: this.config.jurisdictionalFeatureLayer.portalItemId },
            title: "Jurisdictional Units",
            outFields: ["*"],  //TODO: Fix hardcode
            visible: false,
            popupTemplate: { // autocasts as new PopupTemplate()
              title: "{JurisdictionalUnitName}",
              content: "<div class=\"popup-incident-primary-fields\">Landowner Kind: {LandownerKind}</br>Landowner Category: {LandownerCategory}</br>Unit ID: {JurisdictionalUnitID_sansUS}</br>Unit Name: {JurisdictionalUnitName}</br></div>",
              overwriteActions: true
            },
            opacity: .5,
            labelsVisible: true,
            legendEnabled: false,
            renderer: this.jurisdictionalRenderer
          };


          const perimeterConfig = {
            visible: false,
            title: "NIFS Perimeters",   //TODO: Fix layername hardcode
            //definitionExpression: "1=2",
            outFields: ["*"],
            renderer: {
              type: "simple",  // autocasts as new SimpleRenderer()
              symbol: symbols.selectedPerimeterSymbol
            }
          };

          const fireHistoryConfig = {
            portalItem: this.config.fireHistoryLayer.jsInit[this.config.env].portalItem,
            visible: false,
            title: "Working Perimeters",
            opacity: 0.5,
            outFields: ["IncidentName", "FeatureCategory", "MapMethod", "Comments", "GISAcres", "DeleteThis", "FeatureAccess", "FeatureStatus", "IRWINID", "Source"],
            renderer: {
              type: "simple",
              symbol: symbols.selectedPerimeterSymbol
            }
          };

          const finalFireConfig = {
            portalItem: this.config.finalFireFeatureLayer.jsInit[this.config.env].portalItem,
            visible: false,
            title: "Final Fire Perimeters", 
            opacity: 0.5,  
            outFields: ["IncidentName","FeatureCategory","MapMethod","GISAcres","FeatureStatus","IRWINID"],
            renderer: {
              type: "simple", 
              symbol: symbols.selectedPerimeterSymbol
            }
          };
          this.jurisdictionalFeatureLayer = new FeatureLayer(Object.assign({}, jurisdictionalFeatureConfig, this.config.jurisdictionalFeatureLayer));
          this.jurisdictionalFeatureLayer.listMode = "hide";
          this.jurisdictionalFeatureLayer.title = consts.layerTitles.jurisdictionalUnitsFeatureLayer;

          this.gaccFeatureLayer = new FeatureLayer(
            {
              url: this.config.referenceLayerEndpoints.gacc,  
              title: "GACC",
              outFields: ["GACCName", "GACCUnitID", "GACCAbbreviation", "ContactPhone"],  //TODO: Fix hardcode
              popupTemplate: { // autocasts as new PopupTemplate()
                title: "{GACCName}",
                content: "<div class=\"popup-incident-primary-fields\">Name: {GACCName}</br>Unit ID: {GACCUnitID}</br>Abbreviation: {GACCAbbreviation}</br>Contact Phone: {ContactPhone}</div>",
                overwriteActions: true
              },
              popupEnabled: true,
              visible: false,
              opacity: 0.4,
              labelsVisible: true,
              legendEnabled: true,
              renderer: {
                type: "simple",
                symbol: symbols.gaccSymbol
              }

            });

          this.viirsFeatureLayer = new FeatureLayer(
            {
              url: this.config.referenceLayerEndpoints.viirs,  
              title: "VIIRS",
              outFields: ["OBJECTID",
                "satellite",
                "esritimeutc",
                "latitude",
                "longitude",
                "FRP",
                "confidence",
                "hours_old"],
              visible: false,
              popupEnabled: true,
              popupTemplate: {
                title: "VIIRS",
                expressionInfos:
                  [
                    {
                      name: "arcade-for-satellite",
                      title: "Satellite",
                      expression: "When($feature.satellite == 'N', 'Suomi NPP', $feature.satellite == '1', 'NOAA-20', $feature.satellite)"
                    }
                  ],
                content: [
                  {
                    type: "fields",
                    fieldInfos: [
                      {
                        fieldName: "expression/arcade-for-satellite"
                      },
                      {
                        fieldName: "esritimeutc",
                        label: "Time (UTC)"
                      },
                      {
                        fieldName: "latitude",
                        label: "Latitude"
                      },
                      {
                        fieldName: "longitude",
                        label: "Longitude"
                      },
                      {
                        fieldName: "FRP",
                        label: "Fire Radiative Power"
                      },
                      {
                        fieldName: "confidence",
                        label: "Confidence"
                      },
                      {
                        fieldName: "hours_old",
                        label: "Hours Old"
                      },
                    ]
                  }
                ]
              },
              opacity: 0.6,
              labelsVisible: true,
              legendEnabled: true,
              renderer: {
                type: "class-breaks",
                field: "hours_old",
                classBreakInfos: [
                  {
                    minValue: 48,
                    maxValue: 168,
                    symbol: {
                      type: "simple-marker",
                      style: "circle",
                      color: "#FFFF00",
                      size: "10px"
                    }
                  },
                  {
                    minValue: 24,
                    maxValue: 48,
                    symbol: {
                      type: "simple-marker",
                      style: "circle",
                      color: "#FFA900",
                      size: "10px"
                    }
                  },
                  {
                    minValue: 12,
                    maxValue: 24,
                    symbol: {
                      type: "simple-marker",
                      style: "circle",
                      color: "#FF5500",
                      size: "10px"
                    }
                  },
                  {
                    minValue: 6,
                    maxValue: 12,
                    symbol: {
                      type: "simple-marker",
                      style: "circle",
                      color: "#E64D00",
                      size: "10px"
                    }
                  },
                  {
                    minValue: 0,
                    maxValue: 6,
                    symbol: {
                      type: "simple-marker",
                      style: "circle",
                      color: "#A80000",
                      size: "10px"
                    }
                  }
                ]
              }
            });

          const stateLabelClass = {
            // autocasts as new LabelClass()
            symbol: {
              type: "text",  // autocasts as new TextSymbol()
              color: "black",
              font: {  // autocast as new Font()
                family: "Avenir Next",
                size: 12,
                weight: "bold"
              }
            },
            labelPlacement: "above-center",
            labelExpressionInfo: {
              expression: "$feature.NAME"
            }
          };

          //TODO: Fix Url hardcode and title
          this.stateFeatureLayer = new FeatureLayer(
            {
              url: this.config.referenceLayerEndpoints.state,  
              title: "States",
              popupEnabled: true,
              popupTemplate: { // autocasts as new PopupTemplate()
                title: "{NAME}",
                content: "<div class=\"popup-incident-primary-fields\">State: {NAME}</br></div>",
                overwriteActions: true
              },
              visible: false,
              opacity: 0.4,
              labelsVisible: true,
              legendEnabled: true,
              labelingInfo: [stateLabelClass],
              renderer: {
                type: "simple",
                symbol: symbols.gaccSymbol
              }
            });

          const countyLabelClass = {
            // autocasts as new LabelClass()
            symbol: {
              type: "text",  // autocasts as new TextSymbol()
              color: "black",
              font: {  // autocast as new Font()
                family: "Avenir Next",
                size: 12,
                weight: "bold"
              }
            },
            labelPlacement: "above-center",
            labelExpressionInfo: {
              expression: "$feature.NAME"
            }
          };

          this.countyFeatureLayer = new FeatureLayer(
            {
              url: this.config.referenceLayerEndpoints.county, 
              title: "Counties",
              popupEnabled: true,
              popupTemplate: {
                title: "{NAME}",
                content: "<div class=\"popup-incident-primary-fields\">County: {NAME}</div>",
                overwriteActions: true
              },
              visible: false,
              opacity: 0.4,
              labelsVisible: true,
              legendEnabled: true,
              labelingInfo: [countyLabelClass],
              renderer: {
                type: "simple",
                symbol: symbols.gaccSymbol
              }

            });

          //TODO: Fix Url hardcode and title
          this.dispatchCenterFeatureLayer = new FeatureLayer(
            {
              url: this.config.referenceLayerEndpoints.dispatch,  
              title: "National Dispatch Centers",
              outFields: ["OBJECTID", "DispUnitID", "DispName", "GACCAbbreviation"],
              fields: ["OBJECTID", "DispUnitID", "DispName", "GACCAbbreviation"],
              popupTemplate: { // autocasts as new PopupTemplate()
                title: "{DispName}",
                content: "<div class=\"popup-incident-primary-fields\">Name: {DispName}</br>Unit ID: {DispUnitID}</br></div>",
                overwriteActions: true
              },
              popupEnabled: true,
              visible: false,
              opacity: 0.4,
              labelsVisible: true,
              legendEnabled: true,
              renderer: {
                type: "simple",
                symbol: symbols.dispatchCenterSymbol
              }

            });


          this.jurisdictionalTileLayer = new VectorTileLayer({
            url: this.config.referenceLayerEndpoints.jurisdictionalTileLayer,
            title: "Jurisdictional Units",
            visible: false,
            opacity: .5,
            legendEnabled: true
          });
          this.perimeterFeatureLayer = new FeatureLayer(Object.assign({}, perimeterConfig, this.config.perimeterLayer.jsInit[this.config.env]));
          this.perimeterFeatureLayer.popupEnabled = true;
          this.perimeterFeatureLayer.popupTemplate = {

            title: "{IncidentName}",
            actions: (this.isReadOnly) ? [] :
              [
                {
                  title: "Edit",
                  id: "popup-add-irwinid-nifs",
                  className: "esri-icon-edit"
                }],

            content: [
              {
                type: "fields",
                fieldInfos: [
                  {
                    fieldName: "IncidentName",
                    label: "Incident Name"
                  },
                  {
                    fieldName: "FeatureCategory",
                    label: "Category"
                  },
                  {
                    fieldName: "MapMethod",
                    label: "Map Method"
                  },
                  {
                    fieldName: "IRWINID",
                    label: "IRWIN ID"
                  },
                  {
                    fieldName: "GisAcres",
                    label: "Acres"
                  },
                  {
                    fieldName: "PolygonDateTime",
                    label: "Polygon Date Time"
                  },
                  {
                    fieldName: "FeatureStatus",
                    label: "Feature Status"
                  },
                  {
                    fieldName: "Collector",
                    label: "Collector"
                  }
                ]
              }
            ]
          };


          this.fireHistoryLayer = new FeatureLayer(Object.assign({}, fireHistoryConfig, this.config.fireHistoryLayer.jsInit[this.config.env]));
          this.fireHistoryLayer.popupEnabled = true;
          this.fireHistoryLayer.popupTemplate = {

            
            title: "{IncidentName}",
            actions: 
              [
                {
                  title: "Edit",
                  id: "popup-add-irwinid-firehistory",
                  className: "esri-icon-edit"
                }],
            content: [
              {
                type: "fields",
                fieldInfos: [
                  {
                    fieldName: "IncidentName",
                    label: "Incident Name"
                  },
                  {
                    fieldName: "FeatureCategory",
                    label: "Category"
                  },
                  {
                    fieldName: "MapMethod",
                    label: "Map Method"
                  },
                  {
                    fieldName: "IRWINID",
                    label: "IRWIN ID"
                  },
                  {
                    fieldName: "GisAcres",
                    label: "Acres"
                  },
                  {
                    fieldName: "PolygonDateTime",
                    label: "Polygon Date Time"
                  },
                  {
                    fieldName: "FeatureStatus",
                    label: "Feature Status"
                  },
                  {
                    fieldName: "Collector",
                    label: "Collector"
                  }
                ]
              }
            ]
          };
         
          this.customGroupLayer = new GroupLayer({
            layers: [],
            opacity: 75,
            title: "User Layers"
          });

          this.customGroupLayer.listMode = "show";

          this.finalFireLayer = new FeatureLayer(Object.assign({}, finalFireConfig, this.config.finalFireFeatureLayer.jsInit[this.config.env]));

          this.perimetersGroupLayer = new GroupLayer({
            layers: [this.finalFireLayer, this.fireHistoryLayer, this.perimeterFeatureLayer],
            title: "Fire Perimeters"
          });

          this.perimetersGroupLayer.listMode = "show";
          //this.finalFireLayer.listMode = "show";

          this.perimeterGraphicsLayer = new GraphicsLayer();
          this.perimeterGraphicsLayer.listMode = "hide";

          this.mergedPerimetersGraphicsLayer = new GraphicsLayer();
          this.mergedPerimetersGraphicsLayer.listMode = "hide";

          this.originSelectionGraphicsLayer = new GraphicsLayer();
          this.originSelectionGraphicsLayer.listMode = "hide";

          this.mergeGraphicsLayer = new GraphicsLayer();
          this.mergeGraphicsLayer.listMode = "hide";

          this.perimeterModifiedGraphicsLayer = new GraphicsLayer();
          this.perimeterModifiedGraphicsLayer.listMode = "hide";

          this.spatialFilterGraphicsLayer = new GraphicsLayer();
          this.spatialFilterGraphicsLayer.listMode = "hide";

          this.originSelectionSymbol = new SimpleMarkerSymbol(symbols.selectionSymbol);
          this.mergeSelectionSymbol = new SimpleMarkerSymbol(symbols.mergeSymbol);
          this.originDisabledSymbol = new SimpleMarkerSymbol(symbols.disabledSymbol);

          if (this.customGroupLayer) this.map.add(this.customGroupLayer);
          if (this.gaccFeatureLayer) this.map.add(this.gaccFeatureLayer);
          if (this.dispatchCenterFeatureLayer) this.map.add(this.dispatchCenterFeatureLayer);
          if (this.jurisdictionalFeatureLayer) this.map.add(this.jurisdictionalFeatureLayer); 
          if (this.jurisdictionalTileLayer) this.map.add(this.jurisdictionalTileLayer);
          if (this.perimetersGroupLayer) this.map.add(this.perimetersGroupLayer);
          if (this.viirsFeatureLayer) this.map.add(this.viirsFeatureLayer);
          if (this.countyFeatureLayer) this.map.add(this.countyFeatureLayer);
          if (this.stateFeatureLayer) this.map.add(this.stateFeatureLayer);

          if (this.originFeatureLayer) this.map.add(this.originFeatureLayer);
          
          this.map.add(this.fodrGraphicsLayer);
          this.map.add(this.perimeterGraphicsLayer);
          this.map.add(this.mergedPerimetersGraphicsLayer);
          this.map.add(this.perimeterModifiedGraphicsLayer);
          this.map.add(this.spatialFilterGraphicsLayer);
          this.map.add(this.originSelectionGraphicsLayer);
          this.map.add(this.mergeGraphicsLayer);

          //Yoke JurisdictionalFeatureLayer visiblity (empty symbology) to JurisdictionalTileLayer visibility
          reactiveUtils.watch(() => this.jurisdictionalTileLayer.visible, (visible) => {
            var tileLayer = this.jurisdictionalTileLayer;
            var jurisdictionalUnitsFeatureLayer = tileLayer.parent.allLayers.find(function (layer) {
              return layer.title === consts.layerTitles.jurisdictionalUnitsFeatureLayer;
            });

            if (jurisdictionalUnitsFeatureLayer) {
              jurisdictionalUnitsFeatureLayer.visible = visible;
            }
          });

          //the universe in lat lon
          var latLonUniverse = new Polygon(new SpatialReference(4269));
          latLonUniverse.rings = [[[-179.9999999, -80.1], [-179.9999999, 80.1], [179.9999999, 80.1], [179.999999, -80.1], [-179.99999999, -80.1]]];

          //the universe in web mercator
          this.universeWebMercator = webMercatorUtils.geographicToWebMercator(latLonUniverse);

          this.view.whenLayerView(this.originFeatureLayer).then(
            layerview => {
              window.api.setOriginLayerUrl(this.originFeatureLayer.url + '/' + this.originFeatureLayer.layerId);
              window.api.setOriginLayerToken(this.originFeatureLayer.portalItem.portal.credential.token);
              this.originLayerView = layerview;
              resolve();
            },
            error => {
              console.log("No Layer view for origin layer")
              reject();
            }
          );


          this.view.watch("scale", (newValue) => {
            if (this.jurisdictionalTileLayer.visible) {
              if (newValue > 72300) {
                this.jurisdictionalFeatureLayer.renderer = {
                  type: "simple",
                  symbol: {
                    "type": "simple-fill",
                    "style": "solid",
                    "color": [0, 0, 0, 0],
                    "outline": {
                      "color": [0, 0, 0, 0],
                      "width": 1
                    }
                  }
                };
                this.jurisdictionalFeatureLayer.refresh();
              }
              else {
                this.jurisdictionalFeatureLayer.renderer = this.jurisdictionalRenderer;
                this.jurisdictionalFeatureLayer.refresh();
              }

            }
            //this.originFeatureLayer.renderer = (parseFloat(newValue) <= 500000) ? this.pointRenderer : this.heatmapRenderer;
            //this.originFeatureLayer.effect = (parseFloat(newValue) >= 500000) ? "bloom(1.3, 0.1px, 15%)" : null;

          });

          //TODO: Change this.
          for (let portalJson of this.config.supportLayers[this.config.env]) {

            Layer.fromPortalItem(portalJson).then(layer => {

              layer.on("layerview-create", function (event) {
                for (var i = 0; i < event.layerView.layer.layers.items.length; i++) {
                  var layer = event.layerView.layer.layers.items[i];
                  if (layer.layerId !== 6) 
                  {
                    layer.visible = false;
                  }
                  else 
                  {
                    layer.definitionExpression = `DeleteThis IS NULL OR DeleteThis = 'NO' OR DeleteThis = 'No'`
                    // Add this action to the popup so it is always available in this view
                    var template = {
                      // autocasts as new PopupTemplate()
                      title: "{IncidentName}",
                      actions: (this.isReadOnly) ? [] :
                        [
                          {
                            title: "Edit",
                            id: "popup-add-irwinid-nifs",
                            className: "esri-icon-edit"
                          }],
                      //TODO: Fix field hardcodes
                      content: [
                        {
                          type: "fields",
                          fieldInfos: [
                            {
                              fieldName: "IncidentName",
                              label: "Incident Name"
                            },
                            {
                              fieldName: "FeatureCategory",
                              label: "Category"
                            },
                            {
                              fieldName: "MapMethod",
                              label: "Map Method"
                            },
                            {
                              fieldName: "IRWINID",
                              label: "IRWIN ID"
                            },
                            {
                              fieldName: "GisAcres",
                              label: "Acres"
                            },
                            {
                              fieldName: "PolygonDateTime",
                              label: "Polygon Date Time"
                            },
                            {
                              fieldName: "FeatureStatus",
                              label: "Feature Status"
                            },
                            {
                              fieldName: "Collector",
                              label: "Collector"
                            }
                          ]
                        }
                      ]
                    };
                    layer.popupTemplate = template;
                  }
                }
              });
            });
          }
    });
  }

  handleAddUserLayerToMap = (args) => {
  
    return new Promise((resolve, reject) => {
   

    //Hack to append NIFC token to anything from NIFC
      const urlToUse = (args.Url.includes("T4QMspbfLg3qTGWY")) ? args.Url + "?token=" + window.api.getToken() : args.Url;
    //T4QMspbfLg3qTGWY
      const userFeatureLayer = new FeatureLayer(
        {
          url: urlToUse,  
          title: args.Title,
          popupEnabled: args.ShowPopups,
          visible: args.ShowDefault,
          opacity: 0.75,
          legendEnabled: true,
          outFields: ["*"], 
        });
        this.customGroupLayer.layers.add(userFeatureLayer);
    });
  }

  handleRemoveUserLayer = (item) => {
    return new Promise((resolve, reject) => 
    {
      var layerToRemove = this.customGroupLayer.layers.items.find(function (layer) {
        return layer.title === item.title;
      });

      this.customGroupLayer.layers.remove(layerToRemove);

      window.api.removeUserLayer(item.title).then( response => {
        resolve();
      });
    
    });
}

  handleAddAndSaveUserLayer = (args) => {
    return new Promise((resolve, reject) => {

    
      //Hack to append NIFC token to anything from NIFC
      const urlToUse = (args.Url.includes("T4QMspbfLg3qTGWY")) ? args.Url + "?token=" + window.api.getToken() : args.Url;
      const userFeatureLayer = new FeatureLayer(
        {
          url: urlToUse,  
          title: args.Title,
          popupEnabled: args.ShowPopups,
          visible: args.ShowDefault,
          opacity: 0.75,
          legendEnabled: true,
          outFields: ["*"], 
        });

        window.api.addUserLayer(args).then( response => {

        });
        //userFeatureLayer.url = urlToUse;
        this.customGroupLayer.layers.add(userFeatureLayer);
    });
  }

  getSymbolForFeature = (feature, renderValue) => {

    switch (renderValue) 
    {
      case 'Certified':
        return symbols.certifiedSymbol;
        case 'CertifiedLowUniqueness':
          return symbols.certifiedLowUniquenessSymbol;

      case 'Complete':
        return symbols.completeSymbol;
        case 'CompleteLowUniqueness':
          return symbols.completeLowUniquenessSymbol;
  
      case 'Incomplete':
        return symbols.defaultIncidentSymbol;
        case 'IncompleteLowUniqueness':
          return symbols.defaultLowUniquenessIncidentSymbol;

      case 'IsQuarantined':
        return symbols.conflictIncidentSymbol;

      case 'PrescribedFire':
        return symbols.prescribedFireSymbol;
        
        case 'PrescribedFireLowUniqueness':
          return symbols.prescribedFireLowUniquenessSymbol;

      case 'NonWildfire':
        return symbols.otherFireSymbol;
        case 'NonWildFireLowUniqueness':
          return symbols.otherFireLowUniquenessSymbol;


      default:
          return symbols.defaultIncidentSymbol;
    }
  }

  getSizeForSymbol = (incidentSize) => {
    if (incidentSize) {
      if (incidentSize <= 0.25) return 10;
      else if (incidentSize <= 10) return 14;
      else if (incidentSize <= 100) return 18;
      else if (incidentSize <= 300) return 22;
      else if (incidentSize <= 1000) return 26;
      else if (incidentSize <= 5000) return 30;
      else if (incidentSize <= 50000) return 34;
      else return 38;
    }
    else {
      return 6;
    }
  }

  getRenderValueForFeature = (feature) => {
    const nonWildfire = (feature.attributes.IncidentTypeCategory !== 'RX' && feature.attributes.IncidentTypeCategory !== 'WF');
    const isComplete = (feature.attributes.StatusID === 1);
    const isCertified = (feature.attributes.StatusID === 3 || feature.attributes.ADSPermissionState === 'CERTIFIED');
    const isDefault = (!isCertified && !isComplete);
    const nonWildFireIncomplete = (nonWildfire && !isComplete && !isCertified);
    const isQuarantined = (feature.attributes.IsQuarantined === 1);
    const prescribed = (feature.attributes.IncidentTypeCategory === 'RX');
    const notUnique = ((feature.attributes.MinUniqueness) && feature.attributes.MinUniqueness <= .25);
    //const notUniqueAndPrescribed = (prescribed && notUnique);

    if (prescribed) {
      return (notUnique) ? 'PrescribedFireLowUniqueness' : 'PrescribedFire';
    }

    if (isCertified) {
      return (notUnique) ? 'CertifiedLowUniqueness' : 'Certified';
    }

    if (isComplete) {
      return (notUnique) ? 'CompleteLowUniqueness' : 'Complete';
    }

    if (isDefault) {
      return (notUnique) ? 'IncompleteLowUniqueness' : 'Incomplete';
    }

    if (nonWildfire) {
      return (nonWildFireIncomplete) ? 'NonWildfire' : 'NonWildfireComplete';
    }
   
    if (isQuarantined) 
    {
      return 'IsQuarantined';
    }

    return 'Incomplete';
  }

  //
  // PUBLIC METHODS
  //
  handleGetOriginDefinitionExpression = (done, failed) => {
    done(this.originFeatureLayer.definitionExpression);
  }

  handleSetCustomFilterDefinitionExpression = (where, done, failed) => {

    if (!where.includes(originFields.isValid)) 
    {
      where += " AND " + originFields.isValid + " = 1";
    }

    this.originFeatureLayer.definitionExpression = where;
    this.setSearchWidgetDefinition(where);
    if (this.originFeatureLayer) 
    {
      //this.originFeatureLayer.definitionExpression = expression;
      //TODO: Refactor this, duplicated in setOriginLayerDefinitionExpression
      const queryUrl = this.originFeatureLayer.url + '/' + this.originFeatureLayer.layerId + '/query';
      window.api.getIncidentCountForQuery(this.view.extent, where, queryUrl).then(featureCount => {
        if (featureCount > 0) 
        {
          window.api.getIncidentsForQuery(this.view.extent, 0, [], where, queryUrl, this.maxCountForIncidents).then(features => 
            {
            
            this.fodrGraphicsLayer.graphics.removeAll();

            for (var i = 0; i < features.length; i++) {
              const currentFeature = features[i];
              var symbol = this.getSymbolForFeature(currentFeature, this.getRenderValueForFeature(currentFeature));
              symbol.size = this.getSizeForSymbol(currentFeature.attributes.IncidentSize);

              const g = new Graphic({
                geometry: new Point({
                  x: currentFeature.geometry.x,
                  y: currentFeature.geometry.y,
                  spatialReference: new SpatialReference(3857)
                }),
                attributes: currentFeature.attributes,
                symbol: symbol,
                popupTemplate: this.originPopupTemplate
              }); 

              this.fodrGraphicsLayer.graphics.add(g);
            }
            
            done(
              {
                total: featureCount, 
                features: features
              });
          
          });
        } 
        else 
        {
          this.fodrGraphicsLayer.graphics.removeAll();
          
          done(
            {
              total: 0, 
              features: []
            });
        }
      });
    }
  }

  handleGetFORIDsForQuery = (spatialFilterId, filterGeometry, resultOffset, results, whereClause, resolve, reject) => 
  {

    //Override filter geometry if we're passing in filterID
    const geometryToUse = (spatialFilterId !== null) ? this.spatialFilterPolygon : filterGeometry;
      const url = this.originFeatureLayer.url + '/' + this.originFeatureLayer.layerId + '/query';
      window.api.GetForIDsForQuery(geometryToUse, resultOffset, results, whereClause, url).then(results => {
        resolve(results);
      });
  }
    
  //TODO: Refactor this entire thing.
  handleSetOriginDefinitionExpression = (filter, done, failed) => {
    this.shouldFilterOnPerimeters = (filter.perimeterMapMethod !== null || filter.perimeterFeatureStatus !== null || filter.perimeterFeatureAccess !== null);


      if (this.originFeatureLayer) {
        let terms = [];
        //add individual where clauses to terms array
        //set clause to null if not applicable
        const fireCauseWhere = (filter.fireCauses.length > 0) ? `( ${originFields.fireCauseId} IN ('${filter.fireCauses.join("', '")}') )` : null;
        terms.push(fireCauseWhere);

        const utcOffsetWhere = (filter.utcOffset) ? `( UtcOffset =  ${filter.utcOffset} )` : null;
        terms.push(utcOffsetWhere);

        //const dataSourcesWhere = (filter.dataSources.length > 0) ? `( ${originFields.dataSources} IN ('${filter.dataSources.join("', '")}') )` : null;
        //terms.push(dataSourcesWhere);

        if (filter.dataSources && filter.dataSources.length > 0) {

          let dataSourcesTerms = []; 
          for (let i = 0; i < filter.dataSources.length; i++) 
            {
              dataSourcesTerms.push("DataSources LIKE '%" + filter.dataSources[i] + "%'" );
            }

          const dataSourcesWhere =  `( ${dataSourcesTerms.join(" OR ")} )`;
          terms.push(dataSourcesWhere);
        }

        const multiRootWhere = (filter.multiRootOnly) ? `( ${originFields.isMultiRoot} = 1)` : null;
        terms.push(multiRootWhere);

        if (filter.irwinRelationships) 
        {
          let relationshipTerms = []; 
          if (filter.irwinRelationships.includes("1")) {
            relationshipTerms.push(" (ProvidingResponseRelationship = 1) ");
          }

          if (filter.irwinRelationships.includes("2")) {
            relationshipTerms.push(" (ComplexRelationship = 1) ");
          }

          if (filter.irwinRelationships.includes("3")) {
            relationshipTerms.push(" (PotentialConflictRelationship = 1) ");
          }

          if (filter.irwinRelationships.includes("4")) {
            relationshipTerms.push(" (MergedRelationship = 1) ");
          }

          if (filter.irwinRelationships.includes("5")) {
            relationshipTerms.push(" (PrescribedEscapeRelationship = 1) ");
          }

          if (filter.irwinRelationships.includes("6")) {
            relationshipTerms.push(" (PostFireRelationship = 1) ");
          }

          let relationshipWhere = "";
          if (relationshipTerms.length > 0) 
          {
            if (relationshipTerms.length === 1) {
              relationshipWhere = relationshipTerms[0];
            } else 
            {
              relationshipWhere = "( " + relationshipTerms.join(" OR ") + " ) ";
            }

            terms.push(relationshipWhere);
          }
        }

        const strategyWhere = (filter.strategies.length > 0) ? `( ${originFields.initialFireStrategy} IN ('${filter.strategies.join("', '")}') )` : null;
        terms.push(strategyWhere);

        const categoryWhere = (filter.categories.length > 0) ? `( ${originFields.incidentTypeCategory} IN ('${filter.categories.join("', '")}') )` : null;
        terms.push(categoryWhere);

        if (!categoryWhere) {
          const incidentTypeKindWhere = `(${originFields.incidentTypeKind} = 'FI' OR (${originFields.incidentTypeKind} = 'FM' AND (${originFields.incidentTypeCategory} = 'FA' OR ${originFields.incidentTypeCategory} = 'OR')))`;
          terms.push(incidentTypeKindWhere);
        }

        const landownerCategoryWhere = (filter.landownerCategories.length > 0) ? `( ${originFields.landownerCategory} IN ('${filter.landownerCategories.join("', '")}') )` : null;
        terms.push(landownerCategoryWhere);

        const acresMin = (filter.fireAcresMin === null) ? null : `( (${originFields.calculatedAcres} >= ${filter.fireAcresMin}) OR (${originFields.incidentSize} >= ${filter.fireAcresMin}) )`;
        terms.push(acresMin);

        if (filter.fireAcresMax !== null) {

          const dailyMax = `((${originFields.incidentSize} is NULL) OR (${originFields.incidentSize} <= ${filter.fireAcresMax}))`;
          const calculatedMax = `((${originFields.calculatedAcres} is NULL) OR (${originFields.calculatedAcres} <= ${filter.fireAcresMax}))`;
          let acresMax = `( ${dailyMax} AND ${calculatedMax} )`;

          terms.push(acresMax);
        }

        const maxDiscoveryDateString = (filter.discoveryDateMax) ? getSQLDateEndOfDay(filter.discoveryDateMax) : null;
        const maxDiscoveryDate = (maxDiscoveryDateString) ? `( ${originFields.discoveryDate} <= '${maxDiscoveryDateString}' )` : null;
        terms.push(maxDiscoveryDate);

        const minDiscoveryDateString = (filter.discoveryDateMin) ? getSQLDate(filter.discoveryDateMin) : null;
        const minDiscoveryDate = (minDiscoveryDateString) ? `( ${originFields.discoveryDate} >=  '${minDiscoveryDateString}' )` : null;
        terms.push(minDiscoveryDate);

        const maxOutDateString = (filter.outDateMax) ? getSQLDateEndOfDay(filter.outDateMax) : null;
        const maxOutDate = (maxOutDateString) ? `( ${originFields.outDate} <=  '${maxOutDateString}' )` : null;
        terms.push(maxOutDate);

        const minOutDateString = (filter.outDateMin) ? getSQLDate(filter.outDateMin) : null;
        const minOutDate = (minOutDateString) ? `( ${originFields.outDate} >=  '${minOutDateString}' )` : null;
        terms.push(minOutDate);

        const maxModifiedString = (filter.modifiedDateMax) ? getSQLDateEndOfDay(filter.modifiedDateMax) : null;
        const maxModifiedDate = (maxModifiedString) ? `( ${originFields.modifiedDate} <=  '${maxModifiedString}' )` : null;
        terms.push(maxModifiedDate);

        const minModifiedDateString = (filter.modifiedDateMin) ? getSQLDate(filter.modifiedDateMin) : null;
        const minModifiedDate = (minModifiedDateString) ? `( ${originFields.modifiedDate} >=  '${minModifiedDateString}' )` : null;
        terms.push(minModifiedDate);

        //IMSR
        const maxImsrDateString = (filter.imsrDateMax) ? getSQLDateEndOfDay(filter.imsrDateMax) : null;
        const maxImsrDate = (maxImsrDateString) ? `( ${originFields.imsrDateMax} <=  '${maxImsrDateString}' )` : null;
        terms.push(maxImsrDate);

        const minImsrDateString = (filter.imsrDateMin) ? getSQLDate(filter.imsrDateMin) : null;
        const minImsrDate = (minImsrDateString) ? `( ${originFields.imsrDateMin} >=  '${minImsrDateString}' )` : null;
        terms.push(minImsrDate);

        const imsrFlagWhere = (filter.inImsr) ? `( ${originFields.inImsr} = 1 )` : null;
        terms.push(imsrFlagWhere);

        const gaccWhere = (filter.gacc && filter.gacc.length > 0) ? `( ${originFields.gacc} IN ('${filter.gacc.join("', '")}') )` : null;
        terms.push(gaccWhere);

        if (filter.responseAgencies && filter.responseAgencies.length > 0) {

          let responseAgenciesTerms = []; 
          for (let i = 0; i < filter.responseAgencies.length; i++) 
            {
              responseAgenciesTerms.push("InitialResponseAgencies LIKE '%" + filter.responseAgencies[i] + "%'" );
            }

          const responseAgenciesWhere =  `( ${responseAgenciesTerms.join(" OR ")} )`;
          console.log(responseAgenciesWhere);
          terms.push(responseAgenciesWhere);
        }

        if (filter.state && filter.state.length > 0) {
          let stateWhere = "";
          if (filter.county === null || filter.county.length === 0) {
            stateWhere = `( ${originFields.state} IN ('${filter.state.join("', '")}') )`
          }
          else {
            stateWhere = (filter.county.length === 1)
              ? `(${originFields.state} = '${filter.state[0]}' AND ${originFields.county} = '${filter.county}')`
              : `(${originFields.state} = '${filter.state[0]}' AND ${originFields.county} IN ('${filter.county.join("', '")}'))`;
          }
          terms.push(stateWhere);
        }

        if (filter.structuresDestroyed !== null) {
          const structuresWhere = (filter.structuresDestroyed) ? `( ${originFields.residencesDestroyed} > 0 )` : `( ${originFields.residencesDestroyed} = 0  OR ${originFields.residencesDestroyed} IS NULL )`
          terms.push(structuresWhere);
        }

        if (filter.injuries !== null) {
          const injuriesWhere = (filter.injuries) ? `( ${originFields.injuries} > 0 )` : `( ${originFields.injuries} = 0  OR ${originFields.injuries} IS NULL )`
          terms.push(injuriesWhere);
        }

         if (filter.uniqueness) {
          const uniquenessIndexWhere = `( MinUniqueness IS NOT NULL AND MinUniqueness <= 0.25)`;
          terms.push(uniquenessIndexWhere);
        } 

        if (filter.perimeterAcreageLandowner && filter.perimeterAcreageOperator && filter.perimeterAcreageNumber)  
        { 
          const acreageField = getAcresFieldName(filter.perimeterAcreageLandowner);
          const perimeterAcreageWhere = `( ${acreageField} ${filter.perimeterAcreageOperator} ${filter.perimeterAcreageNumber} )`;
          terms.push(perimeterAcreageWhere);  
        }

        if (filter.perimeterBeneficialLandowner && filter.perimeterBeneficialOperator && filter.perimeterBeneficialNumber)  
          { 
            const beneAcreageField = getBeneAcresFieldName(filter.perimeterBeneficialLandowner);
            const beneAcreageWhere = `( ${beneAcreageField} ${filter.perimeterBeneficialOperator} ${filter.perimeterBeneficialNumber} )`;
            terms.push(beneAcreageWhere);  
          }

        if (filter.fatalities !== null) {
          const fatalitiesWhere = (filter.fatalities) ? `( ${originFields.fatalities} > 0 )` : `( ${originFields.fatalities} = 0  OR ${originFields.fatalities} IS NULL )`
          terms.push(fatalitiesWhere);
        }

        if (filter.otherStructuresDestroyed !== null) {
          const otherStructuresDestroyedWhere = (filter.otherStructuresDestroyed) ? `( ${originFields.structuresDestroyed} > 0 )` : `( ${originFields.structuresDestroyed} = 0  OR ${originFields.structuresDestroyed} IS NULL )`
          terms.push(otherStructuresDestroyedWhere);
        }

        if (filter.residencesThreatened !== null) {
          const residencesThreatenedWhere = (filter.residencesThreatened) ? `( ${originFields.residencesThreatened} > 0 )` : `( ${originFields.residencesThreatened} = 0  OR ${originFields.residencesThreatened} IS NULL )`
          terms.push(residencesThreatenedWhere);
        }

        if (filter.otherStructuresThreatened !== null) {
          const otherStructuresThreatenedWhere = (filter.otherStructuresThreatened) ? `( ${originFields.otherStructuresThreatened} > 0 )` : `( ${originFields.otherStructuresThreatened} = 0  OR ${originFields.otherStructuresThreatened} IS NULL )`
          terms.push(otherStructuresThreatenedWhere);
        }

        const modifiedBySystemWhere = (filter.modifiedBySystem && filter.modifiedBySystem.length > 0) ? `( ${originFields.modifiedBySystem} IN ('${filter.modifiedBySystem.join("', '")}') )` : null;
        terms.push(modifiedBySystemWhere);

        let createdBySystemWhere = (filter.createdBySystem && filter.createdBySystem.length > 0) ? `( ${originFields.createdBySystem} IN ('${filter.createdBySystem.join("', '")}') )` : null;
        if (!createdBySystemWhere) {
          createdBySystemWhere = `( ${originFields.createdBySystem} NOT IN ('irwinimp') )`;
        }

        terms.push(createdBySystemWhere);

        if (filter.certified !== null)  //TODO: Whatever this was, change it.
        {
          const isFederal = `(${originFields.incidentJurisdictionalAgency} IN ('${consts.federal_POOJurisdictionalAgency.join("', '")}'))`;
          const isNonFederal = `(${originFields.incidentJurisdictionalAgency} IS NULL OR ${originFields.incidentJurisdictionalAgency} NOT IN ('${consts.federal_POOJurisdictionalAgency.join("', '")}'))`;
          const federalHasRequired = `(${originFields.requiredFederal.join(" IS NOT NULL AND ")} IS NOT NULL )`;
          const nonFederalHasRequired = `(${originFields.requiredAllAgencies.join(" IS NOT NULL AND ")} IS NOT NULL )`;

          const federalComplete = `(${isFederal} AND ${federalHasRequired})`;
          const federalIncomplete = `(${isFederal} AND NOT ${federalHasRequired})`;

          const nonFederalComplete = `(${isNonFederal} AND ${nonFederalHasRequired})`;
          const nonFederalIncomplete = `(${isNonFederal} AND NOT ${nonFederalHasRequired})`;

          const certifiedWhere = `( ${originFields.certifiedField} = '${originFields.certifiedCodedValue}' AND IsValid = 1 AND ${originFields.isQuarantined} = 0)`;
          const completeWhere = `(  NOT ${certifiedWhere} AND (${federalComplete} OR ${nonFederalComplete}) AND IsValid = 1 AND ${originFields.isQuarantined} = 0)`;
          const incompleteWhere = `((${federalIncomplete} OR ${nonFederalIncomplete}) AND IsValid = 1 AND ${originFields.isQuarantined} = 0 AND ${originFields.certifiedField} <> '${originFields.certifiedCodedValue}')`;

          const isQuarantinedOnlyWhere = `(${originFields.isQuarantined} = 1  AND IsValid = 1)`;
          const isNotQuarantinedOnlyWhere = `(${originFields.isQuarantined} = 0  AND IsValid = 1)`;

          var certified = filter.certified;
          let foundCert = false;

          let certifiedSubterms = [];

          if (certified.includes("ALL") || !certified) {
            const allStatusTypes = `(${originFields.isQuarantined} IN (0,1) AND IsValid = 1)`;
            terms.push(allStatusTypes);
          }
          else {
            for (let i = 0; i < certified.length; i++) {
              if (certified[i] === consts.incidentStatus.certified) {
                certifiedSubterms.push(certifiedWhere);
                foundCert = true;
              }

              if (certified[i] === consts.incidentStatus.complete) {
                certifiedSubterms.push(completeWhere);
                foundCert = true;
              }

              if (certified[i] === consts.incidentStatus.incomplete) {
                certifiedSubterms.push(incompleteWhere);
                foundCert = true;
              }

              if (certified[i] === consts.incidentStatus.conflict) {
                certifiedSubterms.push(isQuarantinedOnlyWhere);
                foundCert = true;
              }

              if (certified[i] === 'IsNotQuarantined') {
                certifiedSubterms.push(isNotQuarantinedOnlyWhere);
                foundCert = true;
              }
            }

            if (!foundCert) {
              const allStatusTypes = `(${originFields.isQuarantined} IN (0,1) AND IsValid = 1)`;
              terms.push(allStatusTypes);
            }
            else {
              if (certified.length === 1) {
                terms.push("(" + certifiedSubterms[0] + ")  AND IsValid = 1");
              }
              else {
                let certifiedSubClause = "(" + certifiedSubterms.join(" OR ") + " AND IsValid = 1)";
                terms.push(certifiedSubClause);
              }
            }
          }
        }
        else {
          const allStatusTypes = `(${originFields.isQuarantined} IN (0,1) AND IsValid = 1)`;
          terms.push(allStatusTypes);
        }

        if (filter.narrativeFilter) {
          const reportWhere = `( ${filter.narrativeFilter} )`;
          terms.push(reportWhere);
        }

        if (filter.incidentJurisdictionalAgency) {
          const incidentJurisdictionalAgencyWhere = `( UPPER(${originFields.incidentJurisdictionalAgency}) LIKE '%${filter.incidentJurisdictionalAgency.toUpperCase()}%' )`;
          terms.push(incidentJurisdictionalAgencyWhere);
        }

        if (filter.dispatchCenterID) {
          const dispatchCenterIDWhere = `( UPPER(${originFields.pooDispatchCenterID}) LIKE '${filter.dispatchCenterID.toUpperCase()}%' )`;
          terms.push(dispatchCenterIDWhere);
        }

        if (filter.jurisdictionalUnit) {
          const jurisdictionalUnitWhere = `( UPPER(${originFields.jurisdictionalUnit}) LIKE '%${filter.jurisdictionalUnit.toUpperCase()}%' )`;
          terms.push(jurisdictionalUnitWhere);
        }

        if (filter.pooProtectingUnit) {
          const pooProtectingUnitWhere = `( UPPER(${originFields.pooProtectingUnit}) LIKE '%${filter.pooProtectingUnit.toUpperCase()}%' )`;
          terms.push(pooProtectingUnitWhere);
        }

        if (filter.pooProtectingAgency) {
          const pooProtectingAgencyWhere = `( UPPER(${originFields.pooProtectingAgency}) LIKE '%${filter.pooProtectingAgency.toUpperCase()}%' )`;
          terms.push(pooProtectingAgencyWhere);
        }

        let overrideUniqueIdWhere = null;
        if (filter.uniqueFireIds && filter.uniqueFireIds.length > 0) {
          overrideUniqueIdWhere = `( ${originFields.forId} IN ('${filter.uniqueFireIds.join("', '")}') )`
        }

        
          const validTerms = terms.filter(cause => cause !== null);
          let where = (validTerms.length === 0) ? this.config.originLayer.defaultWhere : validTerms.join(" AND ");

          if (overrideUniqueIdWhere) 
          {
            where = (where) ? `(${where}) OR ${overrideUniqueIdWhere}` : null;
          }
          
          this.setSearchWidgetDefinition(where);
          let spatialGeometry;
          if (filter.geometry) 
          {
            const rings = (filter.geometry.rings) ? filter.geometry.rings : filter.geometry.geometry.rings;
            const poly = new Polygon({
              rings: rings,
              spatialReference: { wkid: 3857 }
            }); 

            this.handleSetSpatialFilterPolygon(poly);
            spatialGeometry = filter.geometry;
            this.queryForFeatures(where, spatialGeometry, done);
          } 
          else if (filter.hasSpatialFilter)
          {
            window.api.getSpatialFilterById(filter.spatialFilterID).then( response => {
              
              //this.props.setFilterGeometry(response.geometry);
              
              spatialGeometry = response.geometry;

              const poly = new Polygon({
                rings: response.geometry.rings,
                spatialReference: { wkid: 3857 }
              }); 

              this.handleSetSpatialFilterPolygon(poly);
              this.queryForFeatures(where, spatialGeometry, done);
            });
          } 
          else
          {
            this.spatialFilterGraphicsLayer.removeAll();
            var extents = this.view.extent.normalize();
            spatialGeometry = this.view.extent;
            if (extents.length > 1) 
            {
              this.queryForMultiExtentFeatures(where, extents[0], extents[1], done); 
            } 
            else  
           {
              this.queryForFeatures(where, extents[0], done);
          }
          }
      }
      else {
        failed('layer not found');
      }
  }

  setSearchWidgetDefinition = (whereClause) => 
  {
    var mySource = this.searchWidget.sources.items[0];
    mySource.filter = { where: whereClause};
  } 

    queryForFeatures = (where, spatialGeometry, done) => {


        this.originFeatureLayer.definitionExpression = where;

          const queryUrl = this.originFeatureLayer.url + '/' + this.originFeatureLayer.layerId + '/query';
          window.api.getIncidentCountForQuery(spatialGeometry, where, queryUrl).then(featureCount => {
            if (featureCount > 0) 
            {
              window.api.getIncidentsForQuery(spatialGeometry, 0, [], where, queryUrl, this.maxCountForIncidents).then(features => 
                {
                
                this.fodrGraphicsLayer.graphics.removeAll();

                for (var i = 0; i < features.length; i++) {
                  const currentFeature = features[i];
                  var symbol = this.getSymbolForFeature(currentFeature, this.getRenderValueForFeature(currentFeature));
                  symbol.size = this.getSizeForSymbol(currentFeature.attributes.IncidentSize);

                  const g = new Graphic({
                    geometry: new Point({
                      x: currentFeature.geometry.x,
                      y: currentFeature.geometry.y,
                      spatialReference: new SpatialReference(3857)
                    }),
                    attributes: currentFeature.attributes,
                    symbol: symbol,
                    popupTemplate: this.originPopupTemplate
                  }); 
  
                  this.fodrGraphicsLayer.graphics.add(g);
                } 
                done(
                  {
                    total: featureCount, 
                    features: features
                  });
               
              });
            } 
            else 
            {
              this.fodrGraphicsLayer.graphics.removeAll();
              
              done(
                {
                  total: 0, 
                  features: []
                });
            }
          });
  }

  queryForMultiExtentFeatures = (where, extent1, extent2, done) => {


        this.originFeatureLayer.definitionExpression = where;

        const queryUrl = this.originFeatureLayer.url + '/' + this.originFeatureLayer.layerId + '/query';
        
        this.fodrGraphicsLayer.graphics.removeAll();

        let totalFeatureCount = 0;
        var totalFeatures = [];

        window.api.getIncidentCountForQuery(extent1, where, queryUrl).then(featureCount1 => {
          totalFeatureCount += featureCount1;
          window.api.getIncidentsForQuery(extent1, 0, [], where, queryUrl, this.maxCountForIncidents).then(features1 => 
          {
            for (var i = 0; i < features1.length; i++) {
              const currentFeature = features1[i];
              var symbol = this.getSymbolForFeature(currentFeature, this.getRenderValueForFeature(currentFeature));
              symbol.size = this.getSizeForSymbol(currentFeature.attributes.IncidentSize);

              const g = new Graphic({
                geometry: new Point({
                  x: currentFeature.geometry.x,
                  y: currentFeature.geometry.y,
                  spatialReference: new SpatialReference(3857)
                }),
                attributes: currentFeature.attributes,
                symbol: symbol,
                popupTemplate: this.originPopupTemplate
              }); 
              
              totalFeatures.push(currentFeature);
              this.fodrGraphicsLayer.graphics.add(g);
            }
            
            window.api.getIncidentCountForQuery(extent2, where, queryUrl).then(featureCount2 => {
                totalFeatureCount += featureCount2;
                window.api.getIncidentsForQuery(extent2, 0, [], where, queryUrl, this.maxCountForIncidents).then(features2 => 
                  {
                  
                  //this.fodrGraphicsLayer.graphics.removeAll();
    
                  for (var i = 0; i < features2.length; i++) {
                    const currentFeature = features2[i];
                    var symbol = this.getSymbolForFeature(currentFeature, this.getRenderValueForFeature(currentFeature));
                    symbol.size = this.getSizeForSymbol(currentFeature.attributes.IncidentSize);
    
                    const g = new Graphic({
                      geometry: new Point({
                        x: currentFeature.geometry.x,
                        y: currentFeature.geometry.y,
                        spatialReference: new SpatialReference(3857)
                      }),
                      attributes: currentFeature.attributes,
                      symbol: symbol,
                      popupTemplate: this.originPopupTemplate
                    }); 
                    
                    totalFeatures.push(currentFeature);
                    this.fodrGraphicsLayer.graphics.add(g);
                  }
                                            
                done(
                  {
                    total: totalFeatureCount, 
                    features: totalFeatures
                  });

                });
              })
          });
    });
}

  handleGetOriginFields = (done, failed) => {
      if (this.originFeatureLayer) {
        const fields = this.originFeatureLayer.fields.concat(
          [
            new Field({
              alias: "Latitude",
              name: "y",
              type: "double"
            }),
            new Field({
              alias: "Longitude",
              name: "x",
              type: "double"
            })
          ]
        );
        done(fields);
      }
      else {
        failed('Point of origin layer not found');
      }
  }

  handleOriginWithinPerimeter = (perimeter, x, y, done, failed) => {

      const incidentGeometry = new Point({
        x: x,
        y: y,
        spatialReference: new SpatialReference(4269)
      });

      if (perimeter === undefined || perimeter === null || incidentGeometry === undefined || incidentGeometry === null) 
      {
        done(false);
      }
      else {
        let perimeterPolygon = new Polygon(perimeter.geometry);
        const wasContained = perimeterPolygon.contains(incidentGeometry);
        done(wasContained);
      }
  }

  handleOriginUpdateEnd = (cb, done, failed) => {

    if (this.originLayerView) {
      let handle = this.originLayerView.watch('updating', isUpdating => {
        if (isUpdating) {
          cb({ updating: true }, handle);
        }
        else {
          let query = this.originFeatureLayer.createQuery();
          query.returnGeometry = true;

          this.originLayerView.queryFeatures(query).then(
            results => {

              const filterResultsForPerimeter = (this.shouldFilterOnPerimeters && this.perimeterFilterIrwinIds && this.perimeterFilterIrwinIds.length > 0);

              const features = results ? results.features : [];
              const inViewGraphics = features.filter(graphic => this.view.extent.contains(graphic.geometry));
              const graphics = features.map(graphic => {
                return {
                  attributes: graphic.attributes,
                  geometry: graphic.geometry
                }
              });

              const visibleGraphics = inViewGraphics.map(graphic => {
                return {
                  attributes: graphic.attributes,
                  geometry: graphic.geometry
                }
              });

              cb({
                updating: false,
                graphics: (filterResultsForPerimeter) ? graphics.filter(graphic => this.perimeterFilterIrwinIds.includes(graphic.attributes[originFields.irwinId])) : graphics,
                visibleGraphics: (filterResultsForPerimeter) ? visibleGraphics.filter(graphic => this.perimeterFilterIrwinIds.includes(graphic.attributes[originFields.irwinId])) : visibleGraphics
              },
                handle
              )
            });
        }
      });


    }
    else {
      failed('Origin Layer View not found');
    }
  }


  handleGetOriginFeature = (id, done, failed) => {
      if (this.originLayerView) {

        let query = new Query({
          outFields: ['*'],
          where: `(${originFields.irwinId} = '${id}' AND ${originFields.isQuarantined} IN (0,1) AND IsValid = 1)`,
          returnGeometry: true,
          outSpatialReference: this.view.spatialReference.toJSON()
        });

        const url = this.originApiItem.url + '/0';


        executeQuery(url, query).then(results => {
          let feature = null;

          if (results.features.length === 1) {

            const graphic = results.features[0];
            let geomWGS84 = webMercatorUtils.webMercatorToGeographic(graphic.geometry);
            feature = {
              attributes: Object.assign({}, graphic.attributes,
                {
                  "_pointGeometryLatitude": geomWGS84.y,    //TODO: Can we just nuke _pointGeometryLatitude ?
                  "_pointGeometryLongitude": geomWGS84.x
                }
              ),
              geometry: graphic.geometry.toJSON()
            };
          }
          done(feature);
        });
      }
  }

  handleGetOriginFeatures = (geometry, done, failed) => {
      const query = (!geometry) ? null : new Query({
        geometry: geometry,
        spatialRelationship: "contains",
        returnGeometry: true
      });
      if (this.originLayerView) {
        const filterResultsForPerimeter = (this.shouldFilterOnPerimeters && this.perimeterFilterIrwinIds && this.perimeterFilterIrwinIds.length > 0);

        this.originLayerView.queryFeatures(query).then(
          results => {

            const featureToMapFrom = (filterResultsForPerimeter) ? results.features.filter(graphic => this.perimeterFilterIrwinIds.includes(graphic.attributes[originFields.irwinId])) : results.features;

            done({
              graphics: featureToMapFrom.map(graphic => {
                return {
                  attributes: graphic.attributes,
                  geometry: graphic.geometry
                }
              })
            })
          }
        );
      }
      else {
        failed('Origin Layer View null');
      }

  }

  handleGetFullOriginFeaturesFromApi = async (ids, done, failed) => {

      const query = new Query({
        where: `( ${originFields.irwinId} IN ('${ids.join("', '")}') AND ${originFields.isQuarantined} IN (0,1) AND IsValid = 1)`,
        outFields: ['*'],
        returnGeometry: true
      });

      const url = this.originApiItem.url + '/0';

      executeQuery(url, query).then(results => {
        done({
          graphics: (!results.features) ? [] : results.features.map(graphic => {
            return {
              attributes: Object.assign({}, graphic.attributes,
                {
                  "_pointGeometryLatitude": graphic.geometry.y,
                  "_pointGeometryLongitude": graphic.geometry.x
                }
              ),
              geometry: null
            }
          })
        });

      });
  }


  handleClearMergedPerimeterGraphics = (done, failed) => {
    if (!this.mergedPerimetersGraphicsLayer) {
      failed();
      return;
    }

    this.mergedPerimetersGraphicsLayer.removeAll();
    done();
  }

  
  handleAddMergedPerimeterGraphic = (perimeter, incident, done, failed) => {

      if (!this.mergedPerimetersGraphicsLayer) {
        failed();
        return;
      }

      //this.mergedPerimetersGraphicsLayer.removeAll();
      this.mergedPerimetersGraphicsLayer.add({
        geometry: jsonUtils.fromJSON(perimeter.geometry),
        attributes: perimeter.attributes,
        symbol: symbols.mergedPerimeterSymbol
      });

      const textSymbol = new TextSymbol({
        angle: 0,
        backgroundColor: [250,250,250,1],
        borderLineColor: [9,10,0,1],
        color: [0,0,0,1],
        font: {
          decoration: "none",
          family: "Arial",
          size: 12,
          style: "normal",
          weight: "bold"
        },
        haloColor: [255,255,255,1],
        haloSize: 0,
        horizontalAlignment: "center",
        kerning: false,
        lineWidth: 192,
        rotated: false,
        text: perimeter.attributes.IncidentName + "\n Merge",
        verticalAlignment: "bottom",
        xoffset: 0,
        yoffset: 0
      });

      this.mergedPerimetersGraphicsLayer.add({
        geometry: jsonUtils.fromJSON(perimeter.geometry),
        symbol: textSymbol
      });

      done();
  }



  handleSetMergeGraphic = (point, done, failed) => {

    //this.originSelectionGraphicsLayer.removeAll();

    this.mergeGraphicsLayer.add({
      geometry: point,
      symbol: this.mergeSelectionSymbol
    });

    done();
  }

  handleSetOriginSelectionGraphic = (point, done, failed) => {

    this.originSelectionGraphicsLayer.removeAll();

    this.originSelectionGraphicsLayer.add({
      geometry: point,
      symbol: this.originSelectionSymbol
    });

    done();
  }

  handleSetOriginSelectionGraphics = (args, done, failed) => {

      if (args.clearExisting) 
      {
        this.originSelectionGraphicsLayer.removeAll();
      }
      
      if (args.graphics) 
      {
        args.graphics.map(graphic => {
          if (graphic && (graphic.geometry)) 
          {
            const point = new Point({
              x: graphic.geometry.x,
              y: graphic.geometry.y,
              spatialReference:  {
                wkid: 3857
              }
            });

            return this.originSelectionGraphicsLayer.add({
              geometry: point,
              attributes: graphic.attributes,
              symbol: (args.useDisabledSymbol) ? this.originDisabledSymbol : this.originSelectionSymbol
            });
          }
          else 
          {
            return null;
          }

        });
      }

      done();
  }

  handleClearMergeGraphics = (done, failed) => {
    this.mergeGraphicsLayer.removeAll();
    done();
}

  handleClearOriginSelectionGraphics = (args, done, failed) => {
        if (args.clearExisting) {
          this.originSelectionGraphicsLayer.removeAll();
        }
        else if (args.graphics && args.graphics.length > 0) {
          const selectedUniqueIds = args.graphics.map(graphic => { return (graphic) ? graphic.attributes[originFields.forId] : null; });
          this.originSelectionGraphicsLayer.graphics.removeMany(
            this.originSelectionGraphicsLayer.graphics.filter(graphic => {
              return (graphic.attributes) ? selectedUniqueIds.includes(graphic.attributes[originFields.forId]) : true;
            })
          );
        }
        done();
  }

  handleSetOriginVisible = (visible, done, failed) => {
    if (!this.originFeatureLayer) {
      failed();
      return;
    }

    this.originFeatureLayer.visible = visible;
    this.fodrGraphicsLayer.visible = visible;
    done();
  }

  handleClearPerimeterGraphics = (done, failed) => {
    if (!this.perimeterGraphicsLayer) {
      failed();
      return;
    }

    this.perimeterGraphicsLayer.removeAll();
    done();
  }


  handleSetPerimeterGraphic = (perimeter, done, failed) => {

      if (!this.perimeterGraphicsLayer) {
        failed();
        return;
      }

      this.perimeterGraphicsLayer.removeAll();
      this.perimeterGraphicsLayer.add({
        geometry: jsonUtils.fromJSON(perimeter.geometry),
        attributes: perimeter.attributes,
        symbol: symbols.selectedPerimeterSymbol
      });
      done();
  }


  handleSetVisiblePerimeters = (args, done, failed) => {
    if (!this.perimeterFeatureLayer) {
      console.log("Could not set visible perimeters, perimeterFeatureLayer not found.")
      failed();
      return;
    }

    this.getCurrentPerimeterFeatures(args.id, args.useFinalService).then(
      results => {
        var targetPerimeterLayer = (args.useFinalService) ? this.finalPerimeterFeatureLayer : this.perimeterFeatureLayer;
        const oidField = targetPerimeterLayer.objectIdField;
        let features = [];
        const foundFeatures = (results.features && results.features.length > 0 && results.features[0] !== undefined);

        if (foundFeatures) {
          features = results.features;
          const oids = results.features.map(f => { return f.attributes[oidField]; });
          const oidWhere = ` ${targetPerimeterLayer.objectIdField} IN (${oids.join(", ")}) `;
          targetPerimeterLayer.visible = true;
          targetPerimeterLayer.definitionExpression = oidWhere;
        }
        else {
          //this.finalPerimeterFeatureLayer.definitionExpression = '';
          //this.finalPerimeterFeatureLayer.visible = false;
          //this.perimeterFeatureLayer.definitionExpression = '';
          //  this.perimeterFeatureLayer.visible = false;
        }
        done({ features: (args.returnFeatures) ? features : null })
      },
      error => {
        //this.finalPerimeterFeatureLayer.definitionExpression = '';
        //this.finalPerimeterFeatureLayer.visible = false;
        //  this.perimeterFeatureLayer.definitionExpression = '';
        //  this.perimeterFeatureLayer.visible = false;
        failed();
      }

    )
  }

  handleClearVisiblePerimeters = (args, done, failed) => {
    if (this.perimeterFeatureLayer) {
      //  this.perimeterFeatureLayer.visible = false;
      //    this.perimeterFeatureLayer.definitionExpression = "1=2";
      //    this.perimeterFeatureLayer.refresh();

      //this.finalPerimeterFeatureLayer.visible = false;
      //  this.finalPerimeterFeatureLayer.definitionExpression = "1=2";
      //this.finalPerimeterFeatureLayer.refresh();
    }
    done();
  }

  handleSetPerimetersSymbol = (args, done, failed) => {
      if (this.perimeterFeatureLayer) {
        const renderer = new SimpleRenderer({ symbol: args.symbol });
        this.perimeterFeatureLayer.renderer = renderer;
        this.perimeterFeatureLayer.refresh();
      }

      if (this.finalPerimeterFeatureLayer) {
        const renderer = new SimpleRenderer({ symbol: args.symbol });
        this.finalPerimeterFeatureLayer.renderer = renderer;
        this.finalPerimeterFeatureLayer.refresh();
      }
      done();
  }

  getCurrentPerimeterFeatures = (irwinId, useFinalService) => {
    return new Promise((resolve, reject) => {

        resolve({ feature: [] });

        //TODO: Resolve Perimeter issues
        if (!this.perimeterFeatureLayer || (useFinalService && !this.finalPerimeterFeatureLayer)) {
          resolve({ feature: [] });
          return;
        }
        //remove guid braces { }
        let idStore = {};

        const formattedId = irwinId.replace(/\{|\}/g, '');
        idStore[irwinId] = { maxDate: -1 };

        let idWhere = `( ${uncertifiedPerimeterFields.irwinId} = '${formattedId}' OR ${uncertifiedPerimeterFields.irwinId} = '${irwinId}')`;
        if (!useFinalService) {
          idWhere += " AND (DeleteThis IS NULL OR DeleteThis = 'NO' OR DeleteThis = 'No')"
        }
        const perimeterTypes = uncertifiedPerimeterFields.perimeterTypeFeatureCategories;
        const featureCategoryWhereWhere = `${uncertifiedPerimeterFields.featureCategory} IN ('${perimeterTypes.join("', '")}')`

        //where: `${idWhere}  AND ${featureCategoryWhereWhere} AND SHAPE IS NOT NULL`,

        const query = new Query({
          where: `${idWhere} AND ${featureCategoryWhereWhere}`,
          outFields: ["*"],
          returnGeometry: true
        });
        const url = (useFinalService) ? (this.finalPerimeterFeatureLayer.url + '/' + this.finalPerimeterFeatureLayer.layerId) : (this.perimeterFeatureLayer.url + '/' + this.perimeterFeatureLayer.layerId);
        const perimetersToken = (useFinalService) ? this.finalPerimeterFeatureLayer.token : this.perimeterFeatureLayer.token;
        const dateField = uncertifiedPerimeterFields.mostCurrentDateField;

        executeQuery(url, query, { token: perimetersToken }).then(
          results => {
            if (results.features && results.features.length > 0) {
              for (let i = 0; i < results.features.length; i++) {
                const feature = results.features[i];


                if (feature.attributes["FeatureStatus"] === "proposed") {
                  feature.attributes["FeatureStatus"] = "Proposed";
                }

                let irwinId = feature.attributes[uncertifiedPerimeterFields.irwinId].toUpperCase();

                let irwinKey = (irwinId.slice(-1) !== '}' && irwinId.charAt(0) !== '{') ? '{' + irwinId + '}' : irwinId;

                const date = new Date(feature.attributes[dateField]).getTime();
                if (date > idStore[irwinKey].maxDate && (feature.geometry) && feature.geometry.rings.length > 0) {
                  idStore[irwinKey].maxDate = date;
                  idStore[irwinKey].feature = {

                    attributes: Object.assign({}, feature.attributes,
                      {
                        "PerimeterSource": (useFinalService) ? uncertifiedPerimeterFields.finalPerimeterSource : uncertifiedPerimeterFields.nifsPerimeterSource
                      }),
                    geometry: feature.geometry.toJSON()
                  };
                }
              }

              let features = []
              for (var prop in idStore) {
                // skip loop if the property is from prototype
                if (idStore.hasOwnProperty(prop)) {
                  const item = idStore[prop];
                  features.push(item.feature)
                }

              }
              resolve({ features: features });
            }
            else {
              //IMH: No features found. Try again with NIFS if we were checking final, or resolve with nothing.
              if (useFinalService) {
                return this.getCurrentPerimeterFeatures(irwinId, false);
              }
              else {
                resolve({ feature: [] });
              }
            }
          },
          error => {
            reject();
          }
        );
    });
  }

  getPerimeterFeaturesForIrwinIds = async (irwinIds) => {
    return new Promise((resolve, reject) => {
        if (!this.perimeterFeatureLayer) {
          resolve({ feature: [] });
          return;
        }
        let idClauses = [];
        let idStore = {};

        for (var i = 0; i < irwinIds.length; i++) {
          idClauses.push("'" + irwinIds[i].replace(/\{|\}/g, '') + "'");
          idClauses.push("'" + irwinIds[i] + "'");
          idStore[irwinIds[i]] = { maxDate: -1 };
          if (i === 1999) { break; }   //Geoplatform has a 2000 record limit
        }
        //remove guid braces { }
        //const formattedId = irwinId.replace(/\{|\}/g,'');

        const idWhere = `${uncertifiedPerimeterFields.irwinId} IN (${idClauses.join(",")})`;

        const query = new Query({
          where: `${idWhere}`,
          outFields: ["*"],
          returnGeometry: false
        });
        const url = (this.perimeterFeatureLayer.url + '/' + this.perimeterFeatureLayer.layerId);
        const dateField = uncertifiedPerimeterFields.mostCurrentDateField;

        executeQuery(url, query, { token: this.perimeterFeatureLayer.token }).then(
          results => {
            if (results.features && results.features.length > 0) {
              for (let i = 0; i < results.features.length; i++) {
                const feature = results.features[i];
                let irwinId = feature.attributes[uncertifiedPerimeterFields.irwinId].toUpperCase();

                let irwinKey = (irwinId.slice(-1) !== '}' && irwinId.charAt(0) !== '{') ? '{' + irwinId + '}' : irwinId;

                const date = new Date(feature.attributes[dateField]).getTime();
                if (idStore[irwinKey] !== undefined && date > idStore[irwinKey].maxDate) {
                  idStore[irwinKey].maxDate = date;
                  idStore[irwinKey].feature = {

                    attributes: Object.assign({}, feature.attributes,
                      {
                        "PerimeterSource": uncertifiedPerimeterFields.nifsPerimeterSource
                      })
                  };
                }
              }

              let features = []
              for (var prop in idStore) {
                // skip loop if the property is from prototype
                if (idStore.hasOwnProperty(prop)) {
                  const item = idStore[prop];
                  features.push(item.feature)
                }

              }
              resolve({ features: features });
            } else {
              resolve({ features: [] });
            }
          },
          error => {
            reject();
          }
        );
    });
  }

  handleSetModifiedPerimeter = (args, done, failed) => {
      this.perimeterModifiedGraphicsLayer.removeAll();
      if (args.graphics) {
        args.graphics.map(graphic => {
          if (graphic) {
            return this.perimeterModifiedGraphicsLayer.add({
              geometry: jsonUtils.fromJSON(graphic.geometry),
              attributes: graphic.attributes,
              symbol: symbols.selectedPerimeterSymbol
            });
          }
          else {
            return null;
          }

        });
      }

      done();
  }

  handleAddDebugGraphic = (graphic, attributes, isConflict, done, failed) => {
      return this.debugGraphicsLayer.add({
        geometry: jsonUtils.fromJSON(graphic),
        attributes: attributes,
        symbol: (isConflict) ? symbols.debugIncidentGraphic : symbols.debugBlueIncidentGraphic
      });
  }

  handleSetModifiedPerimeterNoClear = (args, done, failed) => {
      if (args.graphics) {
        args.graphics.map(graphic => {
          if (graphic) {
            return this.perimeterModifiedGraphicsLayer.add({
              geometry: jsonUtils.fromJSON(graphic.geometry),
              attributes: graphic.attributes,
              symbol: symbols.selectedPerimeterSymbol
            });
          }
          else {
            return null;
          }

        });
      }

      done();
  }

  handleClearModifiedPerimeter = (args, done, failed) => {
    this.perimeterModifiedGraphicsLayer.removeAll();
    done();
  }

  setSpatialFilterGraphic = (geometryJSON) => {

      if (geometryJSON === null) 
      {
        return;
      }

      this.spatialFilterGraphicsLayer.removeAll();
      let geometry = jsonUtils.fromJSON(geometryJSON)
      if (geometry.spatialReference.isGeographic) 
      {
        geometry = webMercatorUtils.geographicToWebMercator(geometry);
      }

      var maskGeometry = geometryEngine.difference(this.universeWebMercator, geometry);

      this.spatialFilterGraphicsLayer.add({
        geometry: maskGeometry,
        symbol: symbols.maskSymbol
      })
  }

  handleSetSpatialFilterPolygon = (polygon) => {

      this.spatialFilterPolygon = polygon;
      this.spatialFilterGraphicsLayer.removeAll();
       if (polygon === null) 
       {
        return;
       }
       
      this.spatialFilterGraphicsLayer.add({
        geometry: geometryEngine.difference(this.universeWebMercator, polygon),
        symbol: symbols.maskSymbol
      })

  }

  handleOriginRefresh = (done, failed) => {

    if (this.view.type === '2d') 
    {
      this.originFeatureLayer.refresh();
    }
    else 
    {
      if (this.originFeatureLayer.definitionExpression) 
      {
        this.originFeatureLayer.definitionExpression += " AND 1=1";
      }
      else {
        this.originFeatureLayer.definitionExpression = "1=1";
      }
    }
    done();

  }

  getOriginPopupContent = (target) => {
    return new Promise((resolve, reject) => {

        const query = new Query({
          where: `OBJECTID  = '${target.graphic.attributes["OBJECTID"]}'`,
          outFields: ["*"],
          returnGeometry: false
        });
        const url = this.originFeatureLayer.url + '/' + this.originFeatureLayer.layerId;

        executeQuery(url, query).then(
          results => {
            let content;
            if (results && results.features && results.features.length === 1) {
              const attrs = results.features[0].attributes;
              let acres = attrs[originFields.incidentSize];
              const dateFormat = "MM/DD/YYYY";

              if (acres) {
                acres = acres.toFixed(2)
              }

              content = '' +
                '<div class="popup-incident-primary-fields">' +
                '<div class="list-item-detail-group">' +
                '<div class="list-item-field-group">' +
                '<label>Incident Name</label>' +
                '<p class="list-item-incident-name">' + (attrs[originFields.name] || '––') + '</p>' +
                '</div>' +
                '<div class="list-item-field-group">' +
                '<label>ID</label>' +
                '<p class="list-item-detail-label">' + (attrs[originFields.uniqueFireId] || '––') + '</p>' +
                '</div>' +
                '</div>' +
                '<div class="list-item-detail-group">' +
                '<div class="list-item-field-group">' +
                '<label>Incident Size</label>' +
                '<p>' + (acres || '––') + '</p>' +
                '</div>' +
                '<div class="list-item-field-group">' +
                '<label>FireCode</label>' +
                '<p>' + (attrs[originFields.fireCode] || '––') + '</p>' +
                '</div>' +
                '</div>' +
                '<div class="list-item-detail-group">' +
                '<div class="list-item-field-group">' +
                '<label>Discovery</label>' +
                '<p>' + (attrs[originFields.discoveryDate] ? moment(attrs[originFields.discoveryDate]).format(dateFormat) : '––') + '</p>' +
                '</div>' +
                '<div class="list-item-field-group">' +
                '<label>Fire Out</label>' +
                '<p>' + (attrs[originFields.outDate] ? moment(attrs[originFields.outDate]).format(dateFormat) : "––") + '</p>' +
                '</div>' +
                '</div>' +
                '</div>';
            }
            resolve(content);
          },
          error => {
            reject();
          }
        );


    });

  }


  getPerimeterPopup = (target) => {
    return new Promise((resolve, reject) => {

        const query = new Query({
          where: `${originFields.irwinId}  = '${target.graphic.attributes[originFields.irwinId]}'`,
          outFields: ["*"],
          returnGeometry: false
        });

        const url = this.originFeatureLayer.url + '/' + this.originFeatureLayer.layerId;

        executeQuery(url, query).then(
          results => {
            let content;
            if (results && results.features && results.features.length === 1) {
              const attrs = results.features[0].attributes;
              let acres = attrs[originFields.incidentSize];
              const dateFormat = "MM/DD/YYYY";

              if (acres) {
                acres = acres.toFixed(2)
              }

              content = '' +
                '<div class="popup-incident-primary-fields">' +
                '<div class="list-item-detail-group">' +
                '<div class="list-item-field-group">' +
                '<label>Incident Name</label>' +
                '<p class="list-item-incident-name">' + (attrs[originFields.name] || '––') + '</p>' +
                '</div>' +
                '<div class="list-item-field-group">' +
                '<label>ID</label>' +
                '<p class="list-item-detail-label">' + (attrs[originFields.uniqueFireId] || '––') + '</p>' +
                '</div>' +
                '</div>' +
                '<div class="list-item-detail-group">' +
                '<div class="list-item-field-group">' +
                '<label>Incident Size</label>' +
                '<p>' + (acres || '––') + '</p>' +
                '</div>' +
                '<div class="list-item-field-group">' +
                '<label>FireCode</label>' +
                '<p>' + (attrs[originFields.fireCode] || '––') + '</p>' +
                '</div>' +
                '</div>' +
                '<div class="list-item-detail-group">' +
                '<div class="list-item-field-group">' +
                '<label>Discovery</label>' +
                '<p>' + (attrs[originFields.discoveryDate] ? moment(attrs[originFields.discoveryDate]).format(dateFormat) : '––') + '</p>' +
                '</div>' +
                '<div class="list-item-field-group">' +
                '<label>Fire Out</label>' +
                '<p>' + (attrs[originFields.outDate] ? moment(attrs[originFields.outDate]).format(dateFormat) : "––") + '</p>' +
                '</div>' +
                '</div>' +
                '</div>';
            }
            resolve(content);
          },
          error => {
            reject();
          }
        );


    });

  }
}
